import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { useHistory } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  CircularProgress,
} from '@material-ui/core'

import {
  Permitted,
  Select as SelectComponent,
  Card,
  BasePhoneInput,
} from 'components'
import { LoadingFeedback, ProfileSection } from 'components'

import useSnackbar from 'hooks/useSnackbar'
import useAuth from 'hooks/useAuth'

import * as service from 'service'
import { routes } from 'Routes'
import helpers from 'helpers'
import constants from 'constants/index'

import schemaUser from './schema'
import useStyles from './styles'
import { Autocomplete } from '@material-ui/lab'
import useFetch from 'hooks/useFetch'

const UsersForm = ({
  userCompanyId,
  isEdit,
  user,
  userCompany,
  profiles,
  loadingProfiles,
}) => {
  const { DEFAULT_DDI } = constants.countries
  const { company } = useAuth()

  const responsibleProfile =
    userCompany?.profile?.id === constants.user.RESPONSIBLE_PROFILE_ID
  const isSupplier = company?.kind === constants.company.SUPPLIER_KIND
  const enableResponsible = responsibleProfile || isSupplier

  const history = useHistory()
  const snackbar = useSnackbar()
  const classes = useStyles()

  const [loading, setLoading] = useState(false)
  const [profile, setProfile] = useState({})
  const [open, setOpen] = useState(false)

  const { response, isLoading } = useFetch(
    service.dponet.departments.getAll,
    {
      active: true,
      order: 'departments.name ASC',
      allDepartmentsByCompany: true,
      perPage: 1000000,
    },
    [],
    !isSupplier,
  )

  const departments = response?.data?.departments

  const { setValue, handleSubmit, errors, reset, getValues, watch, control } =
    useForm({
      validationSchema: schemaUser(enableResponsible, isSupplier),
      defaultValues: {
        ...user,
        profileId: userCompany?.profile?.id || '',
        phone: user?.phone || DEFAULT_DDI,
        departments: userCompany?.departments || [],
      },
    })

  const handleReturn = () => {
    history.push(routes.user.all)
  }

  const onSubmit = async (data) => {
    try {
      setLoading(true)

      const { phone, ...dataRest } = data

      if (isEdit) {
        if (!isSupplier) {
          await service.dponet.usersCompanies.edit({
            userCompanyId,
            profileId: dataRest.profileId,
          })
        }

        await service.dponet.users.edit({ userId: user.id, user: dataRest })

        Object.keys(user).map((key) => setValue(key, user[key]))

        history.push(routes.user.all)
        snackbar.open({
          message: 'Usuário atualizado com sucesso',
          variant: 'success',
        })
      } else {
        await service.dponet.users.create({
          user: {
            ...dataRest,
            ...(!helpers.validates.phone.isOptional(phone) && {
              phone,
            }),
            profileId: isSupplier
              ? constants.profile.SUPPLIER_ID
              : dataRest.profileId,
          },
        })

        history.push(routes.user.all)
        snackbar.open({
          message: 'Usuário criado com sucesso',
          variant: 'success',
        })
      }
    } catch (error) {
      reset(getValues())
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const selectedProfile = () => {
    if (responsibleProfile) {
      return setProfile(userCompany?.profile)
    }

    const profileId = watch('profileId')
    if (!!profileId && !!profiles) {
      const profile = profiles.find((profile) => profile.id === profileId)

      if (!!profile) {
        return setProfile(profile)
      }
    }

    return setProfile({})
  }

  const navigateToCreateProfile = () => {
    history.push(routes.profiles.new)
  }

  useEffect(() => {
    selectedProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('profileId')])

  return (
    <>
      <LoadingFeedback open={loading || loadingProfiles} />
      <form onSubmit={handleSubmit(onSubmit)} id="user-create-form">
        <Card title="Preencha os dados do usuário">
          <Grid container spacing={2}>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Controller
                control={control}
                as={
                  <TextField
                    label="Nome"
                    type="text"
                    color="primary"
                    variant="outlined"
                    error={!!errors.name}
                    helperText={errors?.name?.message}
                    fullWidth
                  />
                }
                name="name"
                mode="onChange"
              />
            </Grid>
            <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
              <Controller
                control={control}
                as={
                  <TextField
                    label="Email"
                    type="email"
                    color="primary"
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors?.email?.message}
                    fullWidth
                    disabled={isEdit}
                  />
                }
                name="email"
                mode="onBlur"
              />
            </Grid>

            {!isSupplier && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="departments"
                  mode="onChange"
                  onChange={([, data]) =>
                    data.map(({ id, name }) => ({ id, name }))
                  }
                  as={
                    <Autocomplete
                      multiple
                      options={departments ?? []}
                      getOptionLabel={(option) => option.name}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      disabled={isLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label="Departamentos"
                          error={!!errors?.departments}
                          helperText={<>{errors?.departments?.message}</>}
                          disabled={isLoading}
                          InputProps={{
                            ...(isLoading
                              ? {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <CircularProgress size={22} />
                                    </InputAdornment>
                                  ),
                                }
                              : { ...params.InputProps }),
                          }}
                          fullWidth
                        />
                      )}
                    />
                  }
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                control={control}
                as={
                  <BasePhoneInput
                    label="Telefone"
                    error={!!errors.phone}
                    helperText={errors?.phone?.message}
                    fullWidth
                  />
                }
                name="phone"
                mode="onChange"
              />
            </Grid>
          </Grid>
        </Card>
        {!enableResponsible && (
          <Box mt={3}>
            <Typography variant="h4">Perfil de usuário e permissão</Typography>
            <Box mt={1}>
              <>
                <Box className={classes.selectProfileBox}>
                  <Controller
                    control={control}
                    name="profileId"
                    mode="onBlur"
                    as={
                      <SelectComponent
                        label="Selecione o perfil de usuário"
                        items={profiles || []}
                        error={!!errors.profileId}
                        helperText={errors?.profileId?.message}
                      />
                    }
                  />
                </Box>
                <Permitted tag={constants.permissions.PROFILES.CREATE}>
                  <Box pt={1}>
                    <Typography variant="body2" color="textPrimary">
                      Não encontrou a opção? Clique{' '}
                      <Link
                        onClick={navigateToCreateProfile}
                        className={classes.link}
                      >
                        aqui
                      </Link>{' '}
                      para adicionar
                    </Typography>
                  </Box>
                </Permitted>
              </>
            </Box>
            {!isEmpty(profile) && (
              <Box mt={2}>
                <ProfileSection
                  simplifiedProfile={profile}
                  profileId={profile.id}
                />
              </Box>
            )}
          </Box>
        )}

        <Box mt={2} display="flex" justifyContent="flex-start">
          <Box pr={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => handleReturn()}
            >
              Voltar
            </Button>
          </Box>
          <Box>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              form="user-create-form"
            >
              Salvar
            </Button>
          </Box>
        </Box>
      </form>
    </>
  )
}

UsersForm.propTypes = {
  userCompanyId: PropTypes.number,
  isEdit: PropTypes.bool,
  user: PropTypes.object,
  userCompany: PropTypes.object,
}

UsersForm.defaultProps = {
  isEdit: false,
}

export default UsersForm
